import GATSBY_COMPILED_MDX from "/home/runner/work/developmentseed.github.com/developmentseed.github.com/content/project/archived/2014-11-20-healthcare-gov.mdx";
import React from 'react';
import T from 'prop-types';
import styled from 'styled-components';
import {graphql, Link} from 'gatsby';
import get from 'lodash.get';
import {getImage} from 'gatsby-plugin-image';
import {Button} from '@devseed-ui/button';
import {themeVal, glsp, media} from '@devseed-ui/theme-provider';
import {CollecticonBrandGithub, CollecticonExpandTopRight} from '@devseed-ui/collecticons';
import {SupHeading} from '$styles/typography/supheading';
import {leading} from '$styles/typography/leading';
import Constrainer from '$styles/constrainer';
import {Inpage, InpageHeader, InpageHeaderInner, InpageHeadline, InpageMetaDetails, InpageTitle, InpageSuptitle, InpageBody, InpageLead, InpageActions} from '$styles/inpage';
import Layout from '$components/layout';
import {Widont} from '$components/widont';
import Media from '$components/media';
import InpageNavigation from '$components/inpage-nav';
import SectionRelated from '$components/section-related';
import {CallToAction, CTAActions} from '$components/call-to-action';
import {MDXProse} from '$components/mdx-prose';
import Seo from '$components/seo';
import {InpageMDXWrapper} from '$styles/inpage';
const ProjectMeta = (styled(Constrainer))`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: ${glsp(themeVal('layout.gap.xsmall'))};

  ${(media.smallUp)`
    grid-gap: ${glsp(themeVal('layout.gap.small'))};
  `}

  ${(media.mediumUp)`
    grid-template-columns: repeat(8, 1fr);
    grid-gap: ${glsp(themeVal('layout.gap.medium'))};
  `}

  ${(media.largeUp)`
    grid-template-columns: repeat(12, 1fr);
    grid-gap: ${glsp(themeVal('layout.gap.large'))};
  `}

  ${(media.xlargeUp)`
    grid-gap: ${glsp(themeVal('layout.gap.xlarge'))};
  `}
`;
const ProjectMetaInner = (styled.div)`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: ${glsp(1, themeVal('layout.gap.xsmall'))};

  ${(media.smallUp)`
    grid-gap: ${glsp(2, themeVal('layout.gap.small'))};
  `}

  ${(media.mediumUp)`
    grid-template-columns: repeat(8, 1fr);
    grid-gap: ${glsp(4, themeVal('layout.gap.medium'))};
  `}

  ${(media.largeUp)`
    grid-column: 2 / -1;
    grid-template-columns: repeat(11, 1fr);
    grid-gap: ${glsp(5, themeVal('layout.gap.large'))};
  `}

  ${(media.xlargeUp)`
    grid-gap: ${glsp(6, themeVal('layout.gap.xlarge'))};
  `}
`;
const MetaBlock = (styled.section)`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: min-content;
  grid-row-gap: ${glsp()};

  ${(media.mediumUp)`
    grid-row-gap: ${glsp(2)};
  `}
`;
const MetaBlockTitle = (styled(SupHeading).attrs({
  as: 'h1',
  variation: 'base'
}))`
  margin: 0;
`;
const MetaBlockContent = (styled.div)`
  ${leading};
`;
const OverviewBlock = (styled(MetaBlock))`
  grid-column-end: span 4;

  ${(media.mediumUp)`
    grid-column-end: span 8;
  `}

  ${MetaBlockContent} {
    font-size: 1.5rem;
    line-height: 2.25rem;

    ${(media.mediumUp)`
      font-size: 2rem;
      line-height: 2.75rem;
    `}
  }
`;
const ChallengeBlock = (styled(MetaBlock))`
  grid-column-end: span 4;

  ${(media.smallUp)`
    grid-column-end: span 2;
  `}

  ${(media.mediumUp)`
    grid-column-end: span 3;
  `}

  ${(media.largeUp)`
    grid-column-end: span 4;
  `}
`;
const OutcomeBlock = (styled(MetaBlock))`
  grid-column-end: span 4;

  ${(media.smallUp)`
    grid-column-end: span 2;
  `}

  ${(media.mediumUp)`
    grid-column-end: span 3;
  `}

  ${(media.largeUp)`
    grid-column-end: span 4;
  `}
`;
const ProjectSingleTemplate = ({data, location, pageContext, children}) => {
  const {previous, next} = pageContext;
  const {project} = data;
  const {title, lead, client, overview, challenge, topics, outcome, media, public: _public, source, related} = project.frontmatter;
  const coverImage = getImage(media?.cover?.url);
  const coverImageAttribution = get(media, 'cover.attribution');
  const hasMetaSection = !!(overview || challenge || outcome);
  const hasClient = !!client.name;
  const isDevseedClient = hasClient && client.name.toLowerCase() === 'development seed';
  const prevPost = previous ? {
    url: `/projects/${previous.slug}`,
    linkTitle: 'Visit previous project',
    title: previous.frontmatter.title
  } : null;
  const nextPost = next ? {
    url: `/projects/${next.slug}`,
    linkTitle: 'Visit next project',
    title: next.frontmatter.title
  } : null;
  return React.createElement(Layout, {
    location: location
  }, React.createElement(Inpage, null, React.createElement(InpageHeader, {
    leadLayout: "column"
  }, React.createElement(InpageHeaderInner, null, React.createElement(InpageHeadline, null, React.createElement(InpageTitle, null, React.createElement(Widont, null, title)), React.createElement(InpageSuptitle, null, React.createElement(Link, {
    to: "/projects/",
    title: "View Projects page"
  }, "Projects")), hasClient && React.createElement(InpageMetaDetails, null, React.createElement("dt", null, "Client"), React.createElement("dd", null, isDevseedClient ? 'Devseed Lab' : React.createElement(React.Fragment, null, React.createElement("span", null, "With"), ' ', client.url ? React.createElement("a", {
    href: client.url,
    title: "Visit client website"
  }, client.name) : client.name)))), lead && React.createElement(InpageLead, null, React.createElement("p", null, lead)), (_public?.url || source?.url) && React.createElement(InpageActions, null, _public?.url && React.createElement(Button, {
    forwardedAs: "a",
    variation: "primary-fill",
    size: "xlarge",
    href: _public.url,
    title: "See live project"
  }, React.createElement(CollecticonExpandTopRight), " View live"), source?.url && React.createElement(Button, {
    forwardedAs: "a",
    variation: "primary-outline",
    size: "xlarge",
    href: source.url,
    title: "View on GitHub"
  }, React.createElement(CollecticonBrandGithub), " View the code")))), React.createElement(InpageBody, {
    isSoftPadded: !!coverImage
  }, coverImage && React.createElement(Media, {
    size: "cover",
    decoration: "bottom",
    image: coverImage,
    attribution: coverImageAttribution,
    alt: "Project cover image"
  }), hasMetaSection && React.createElement(ProjectMeta, null, React.createElement(ProjectMetaInner, null, overview && React.createElement(OverviewBlock, null, React.createElement(MetaBlockTitle, null, "Overview"), React.createElement(MetaBlockContent, null, React.createElement("p", null, overview))), challenge && React.createElement(ChallengeBlock, null, React.createElement(MetaBlockTitle, null, "Challenge"), React.createElement(MetaBlockContent, null, React.createElement("p", null, challenge))), outcome && React.createElement(OutcomeBlock, null, React.createElement(MetaBlockTitle, null, "Outcome"), React.createElement(MetaBlockContent, null, React.createElement("p", null, outcome))))), React.createElement(InpageMDXWrapper, null, React.createElement(MDXProse, null, children)), !!related?.length && React.createElement(SectionRelated, {
    related: related
  }), React.createElement(InpageNavigation, {
    type: "project",
    topics: topics,
    prevPost: prevPost,
    nextPost: nextPost
  }), React.createElement(CallToAction, {
    title: "Have a challenging project that could use our help?",
    suptitle: "Let's connect"
  }, React.createElement("p", null, "We'd love to hear from you."), React.createElement(CTAActions, null, React.createElement(Button, {
    variation: "primary-fill",
    size: "large",
    forwardedAs: Link,
    to: "/contact"
  }, "Contact us"))))));
};
ProjectSingleTemplate
export default function GatsbyMDXWrapper(props) {
  return React.createElement(ProjectSingleTemplate, props, React.createElement(GATSBY_COMPILED_MDX, props));
}
ProjectSingleTemplate.propTypes = {
  data: T.object,
  location: T.object,
  children: T.node,
  pageContext: T.shape({
    id: T.string,
    next: T.object,
    previous: T.object
  })
};
export const pageQuery = graphql`
  query ProjectById($id: String!) {
    project(id: { eq: $id }) {
      id
      internal {
        type
      }
      frontmatter {
        title
        lead
        teaser
        overview
        challenge
        outcome
        client {
          name
          url
        }
        media {
          ...CoverImageFluid
        }
        topics
        public {
          url
        }
        source {
          title
          url
        }
        related {
          ...RelatedContent
        }
      }
    }
  }
`;
export const Head = ({data}) => {
  const {title, teaser, media} = data.project.frontmatter;
  return React.createElement(Seo, {
    title: title,
    description: teaser,
    image: media?.cover?.url?.publicURL
  });
};
